import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { storeService, Store } from '@/lib/services/store.service';
import { logger } from '@/lib/logger';

interface StoreContextType {
  store: Store | null;
  loading: boolean;
  error: string | null;
  loadStoreByDomain: (domain: string) => Promise<void>;
}

const StoreContext = createContext<StoreContextType | undefined>(undefined);

interface StoreProviderProps {
  children: ReactNode;
}

export function StoreProvider({ children }: StoreProviderProps) {
  const [store, setStore] = useState<Store | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const loadStoreByDomain = async (domain: string) => {
    try {
      setLoading(true);
      setError(null);
      
      logger.log('🔍 Loading store by domain:', domain);
      const storeData = await storeService.getByDomain(domain);
      logger.log('✅ Store loaded:', storeData.name);
      setStore(storeData);
    } catch (err: any) {
      logger.error('❌ Error loading store:', err?.message || 'Unknown error');
      
      if (err.response?.status === 404) {
        setError('Loja não encontrada. Verifique o domínio e tente novamente.');
      } else if (err.message?.includes('JSON')) {
        setError('Erro de comunicação com o servidor. Verifique se a API está rodando.');
      } else {
        setError('Erro ao carregar a loja. Tente novamente mais tarde.');
      }
      
      setStore(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <StoreContext.Provider
      value={{
        store,
        loading,
        error,
        loadStoreByDomain,
      }}
    >
      {children}
    </StoreContext.Provider>
  );
}

export function useStore() {
  const context = useContext(StoreContext);
  if (!context) {
    throw new Error('useStore must be used within StoreProvider');
  }
  return context;
}

export default StoreContext;
